import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Briefcase, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface UpcomingAppointment {
  id: string;
  date: string;
  time: string;
  prospect_name: string | null;
  company_name: string | null;
  job_title: string | null;
  status: string;
  location: string | null;
  title: string | null;
}

interface Props {
  clientId: string;
  onNavigate: (id: string) => void;
}

const UpcomingAppointmentsSlider: React.FC<Props> = ({ clientId, onNavigate }) => {
  const [appointments, setAppointments] = useState<UpcomingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  const fetchUpcoming = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('appointments')
        .select('id, date, time, prospect_name, company_name, job_title, status, location, title')
        .eq('client_id', clientId)
        .in('status', ['planifié', 'confirmé', 'Scheduled', 'Confirmed'])
        .gte('date', today)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(20);

      if (error) throw error;
      setAppointments(data || []);
      setCurrent(0);
    } catch (err) {
      console.error('Error fetching upcoming appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(appointments.length - 1, c + 1));

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-6" />
        <div className="h-40 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Prochains rendez-vous</h2>
        <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl">
          <Calendar className="h-10 w-10 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Aucun rendez-vous a venir</p>
        </div>
      </motion.div>
    );
  }

  const apt = appointments[current];
  const formattedDate = new Date(apt.date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const getDaysUntil = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    d.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Demain';
    return `Dans ${diff} jours`;
  };

  const statusLabel = apt.status === 'confirmé' || apt.status === 'Confirmed' ? 'Confirmé' : 'Planifié';
  const statusColor =
    apt.status === 'confirmé' || apt.status === 'Confirmed'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Prochains rendez-vous</h2>
          <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
            {current + 1} / {appointments.length}
          </span>
        </div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={apt.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }}
              className="bg-gray-50 rounded-xl border border-gray-100 p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {apt.prospect_name || apt.title || 'Rendez-vous'}
                  </h3>
                  {apt.job_title && (
                    <p className="text-sm text-gray-500 mt-0.5">{apt.job_title}</p>
                  )}
                </div>
                <span className={`shrink-0 ml-3 text-xs font-semibold px-2.5 py-1 rounded-full border ${statusColor}`}>
                  {statusLabel}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {apt.company_name && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Briefcase className="h-3.5 w-3.5 text-gray-400 mr-2 shrink-0" />
                    <span className="truncate">{apt.company_name}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-3.5 w-3.5 text-gray-400 mr-2 shrink-0" />
                  <span className="capitalize truncate">{formattedDate}</span>
                </div>
                {apt.time && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="h-3.5 w-3.5 text-gray-400 mr-2 shrink-0" />
                    <span>{apt.time}</span>
                  </div>
                )}
                {apt.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-3.5 w-3.5 text-gray-400 mr-2 shrink-0" />
                    <span className="truncate">{apt.location}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                  {getDaysUntil(apt.date)}
                </span>
                <button
                  onClick={() => onNavigate(apt.id)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Voir la fiche
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {appointments.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-5">
            <button
              onClick={prev}
              disabled={current === 0}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>

            <div className="flex items-center gap-1.5">
              {appointments.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === current ? 'w-6 bg-gray-800' : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              disabled={current === appointments.length - 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default UpcomingAppointmentsSlider;
